import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import * as Styled from './styled'
import { useCanvasScale } from './useCanvasScale'
import { useSelector } from 'react-redux'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { CANVAS_ID } from 'consts'
import { useWebWoodContext } from 'context/WebWoodContext'
import CanvasItemsLayer from './CanvasItemsLayer'

interface IProps {
  canvasWrapperEl: HTMLDivElement | null
}

const FilledCanvas: React.FC<IProps> = ({ canvasWrapperEl }) => {
  const { engine, onCanvasElementReady: onCanvasElementReadyCallback } =
    useWebWoodContext()
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const [canvasReady, setCanvasReady] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const existingCanvasElement = useMemo(() => {
    return engine?.canvas || null
  }, [engine])
  const { width: canvasWidth, height: canvasHeight } = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const scale = useCanvasScale(canvasWrapperEl)

  useEffect(() => {
    if (existingCanvasElement && containerRef.current) {
      canvasRef.current = existingCanvasElement
      containerRef.current.appendChild(existingCanvasElement)
    }
  }, [existingCanvasElement, containerRef, canvasRef])

  useEffect(() => {
    if (!canvasReady || !canvasRef.current) return
    onCanvasElementReadyCallback(canvasRef.current)
  }, [canvasReady, onCanvasElementReadyCallback])

  const onCanvasElementReady = useCallback(
    (canvasElement: HTMLCanvasElement) => {
      if (!canvasElement) return
      resizeObserverRef.current = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect

        if (!canvasReady && width > 0 && height > 0) {
          setCanvasReady(true)
        }
      })
      canvasRef.current = canvasElement
      resizeObserverRef.current.observe(canvasElement)
    },
    [canvasReady]
  )

  return (
    <Styled.ScalableWrapper
      $scale={scale}
      $canvasWidth={canvasWidth}
      $canvasHeight={canvasHeight}
    >
      <Styled.CanvasWrapper ref={containerRef}>
        {!existingCanvasElement ? (
          <Styled.Canvas id={CANVAS_ID} ref={onCanvasElementReady} />
        ) : null}
      </Styled.CanvasWrapper>
      <CanvasItemsLayer canvasScale={scale > 0 ? scale : 1} />
    </Styled.ScalableWrapper>
  )
}

export default memo(FilledCanvas)
